import React, { useEffect, useState } from "react"
import { Check, Copy, ExternalLink, Search, Ticket } from "lucide-react"
import { useParams } from "react-router-dom"
import CaseDetails from "../components/CaseDetails"
import LoadMore from "../components/LoadMore"
import { trackEvent } from "../utils/tracking"

const BONUS_CODE = "CASERTP"
const PAGE_SIZE = 24

const formatRtpUp = (rawRtp) => {
  const parsed = Number.parseFloat(rawRtp)
  if (!Number.isFinite(parsed)) return "0.0"
  return (Math.ceil(parsed * 10) / 10).toFixed(1)
}

const CasesPage = () => {
  const { site } = useParams()

  const [cases, setCases] = useState([])
  const [website, setWebsite] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState("rtp")
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  })
  const [retryKey, setRetryKey] = useState(0)
  const [totalCases, setTotalCases] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setPage(1)
    setSearchQuery("")
    setDebouncedSearch("")
    setWebsite(null)
    setTotalCases(0)
  }, [site])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1)
      setDebouncedSearch(searchQuery.trim())
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [searchQuery])

  useEffect(() => {
    const controller = new AbortController()

    const fetchWebsiteAndCases = async () => {
      setLoading(true)
      setError("")

      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          sort: sortBy,
        })

        if (debouncedSearch) params.set("search", debouncedSearch)

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/websites/${site}?${params.toString()}`,
          { signal: controller.signal }
        )

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`)
        }

        const data = await res.json()
        setWebsite(data.website || null)
        const nextCases = Array.isArray(data.cases) ? data.cases : []
        setCases((currentCases) => {
          if (page === 1) return nextCases

          const uniqueCases = new Map(
            [...currentCases, ...nextCases].map((item) => [
              item._id || `${item.Title}-${item.Website}-${item.subpageLinks}`,
              item,
            ])
          )
          return [...uniqueCases.values()]
        })

        const nextPagination = data.pagination || {
          page: 1,
          pageSize: PAGE_SIZE,
          total: nextCases.length,
          totalPages: 1,
        }
        setPagination(nextPagination)
        setTotalCases(Number(data.totalCases) || Number(nextPagination.total) || nextCases.length)

        if (nextPagination.page !== page) {
          setPage(nextPagination.page)
        }
      } catch (err) {
        if (err.name === "AbortError") return

        console.error("Failed to fetch website and cases:", err)
        if (page === 1) setCases([])
        setError("We couldn't load this website's cases. Please try again later.")
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    fetchWebsiteAndCases()

    return () => controller.abort()
  }, [site, page, sortBy, debouncedSearch, retryKey])

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(BONUS_CODE)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error("Failed to copy bonus code:", err)
    }
  }

  const websiteName = website?.websiteName || "Website"

  const handleLoadMore = () => {
    if (loading || pagination.page >= pagination.totalPages) return
    const nextPage = pagination.page + 1
    if (nextPage === page) {
      setRetryKey((currentKey) => currentKey + 1)
    } else {
      setPage(nextPage)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="relative mb-12 text-center">
        <h1 className="mb-4 font-display text-4xl font-bold bg-gradient-to-r from-[#FF6B00] via-[#FF9A3D] to-[#FFD700] bg-clip-text text-transparent drop-shadow-glow sm:text-5xl">
          {website ? `${websiteName} Cases` : "Explore Cases"}
        </h1>
        <p className="mx-auto max-w-2xl text-blue-100/70">
          Compare prices and RTP, then find the case that gives you the best value.
        </p>
      </header>

      {loading && !website ? (
        <section className="mb-10 animate-pulse rounded-3xl border border-[#FF6B00]/10 bg-[#1A1D21]/50 p-5 sm:p-6">
          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="h-36 w-full rounded-2xl bg-[#0F1215] sm:w-64" />
            <div className="flex-1 space-y-4 py-2">
              <div className="h-7 w-52 rounded bg-[#2A2D31]/70" />
              <div className="h-4 max-w-lg rounded bg-[#2A2D31]/50" />
              <div className="h-11 max-w-sm rounded-xl bg-[#2A2D31]/50" />
            </div>
          </div>
        </section>
      ) : website ? (
        <section className="relative mb-10 overflow-hidden rounded-3xl border border-[#FF6B00]/20 bg-gradient-to-br from-[#FF6B00]/10 via-[#1A1D21]/70 to-[#0F1215]/80 p-5 shadow-[0_0_40px_rgba(255,107,0,0.12)] backdrop-blur-sm sm:p-6">
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#FF6B00]/10 blur-3xl" />

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-stretch">
            <div className="flex min-w-0 flex-col gap-6 sm:flex-row sm:items-center">
              <div className="flex h-36 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#FF6B00]/20 bg-black p-5 shadow-[0_0_24px_rgba(255,107,0,0.08)] sm:w-64">
                <img
                  src={website.websiteLogo || "/placeholder.svg"}
                  alt={`${websiteName} logo`}
                  className="h-full w-full object-contain"
                />
              </div>

              <div className="min-w-0">
                <h2 className="truncate font-display text-3xl font-semibold text-white">
                  {websiteName}
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-6 text-blue-100/70">
                  Browse {totalCases} {totalCases === 1 ? "case" : "cases"} from {websiteName} and compare every option by RTP and price.
                </p>
                <p className="mt-2 text-xs text-blue-100/45">
                  Prices are shown in each website's own currency or coin value.
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  <div className="rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/70 px-4 py-2">
                    <span className="block text-xs text-blue-100/50">Average RTP</span>
                    <span className="font-display text-xl font-semibold text-[#FF9A3D]">
                      {formatRtpUp(website.websiteRTP)}%
                    </span>
                  </div>
                  <div className="rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/70 px-4 py-2">
                    <span className="block text-xs text-blue-100/50">Available cases</span>
                    <span className="font-display text-xl font-semibold text-white">{totalCases}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-2xl border border-[#FF6B00]/15 bg-[#0D0F11]/80 p-4">
              <div className="mb-3 flex items-center gap-2 text-sm text-blue-100/70">
                <Ticket className="h-4 w-4 text-[#FF9A3D]" />
                Use our bonus code
              </div>

              <button
                type="button"
                onClick={handleCopyCode}
                className="group flex w-full items-center justify-between rounded-xl border border-dashed border-[#FF6B00]/40 bg-[#FF6B00]/10 px-4 py-3 text-left transition-colors hover:border-[#FF6B00]/70 hover:bg-[#FF6B00]/15 focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/50"
                aria-label={`Copy bonus code ${BONUS_CODE}`}
              >
                <span className="font-display text-lg font-semibold tracking-[0.12em] text-white">
                  {BONUS_CODE}
                </span>
                <span className="flex items-center gap-1.5 text-xs font-medium text-[#FF9A3D]">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </span>
              </button>

              <a
                href={website.affiliateLink}
                target="_blank"
                rel="noopener noreferrer"
                data-popup-gate="true"
                data-affiliate-outbound="true"
                data-affiliate-website={websiteName}
                className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(255,107,0,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(255,107,0,0.28)] ${
                  !website.affiliateLink ? "pointer-events-none opacity-50" : ""
                }`}
                onClick={() => {
                  try {
                    trackEvent("AffiliateClick", {
                      website: websiteName,
                      url: website.affiliateLink,
                    })
                  } catch (err) {
                    // Tracking should never block navigation.
                  }
                }}
              >
                Visit {websiteName}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      ) : null}

      <section>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-display text-3xl font-semibold text-white">Available Cases</h2>
          {!loading && !error && (
            <span className="w-fit rounded-full border border-[#2A2D31]/60 bg-[#1A1D21]/50 px-3 py-1 text-sm text-blue-100/70">
              {pagination.total} {pagination.total === 1 ? "result" : "results"}
            </span>
          )}
        </div>

        <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-[#2A2D31]/50 bg-[#1A1D21]/40 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-sm md:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">Search cases</span>
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/40" />
            <input
              type="search"
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 py-3 pl-10 pr-4 text-sm text-white outline-none transition-colors placeholder:text-blue-100/40 focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            />
          </label>

          <label className="relative md:w-56">
            <span className="sr-only">Sort cases</span>
            <select
              value={sortBy}
              onChange={(event) => {
                setSortBy(event.target.value)
                setPage(1)
              }}
              className="w-full appearance-none rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 px-4 py-3 pr-10 text-sm text-white outline-none transition-colors focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            >
              <option value="rtp">Highest RTP</option>
              <option value="name">Name (A-Z)</option>
              <option value="price">Price (Low to High)</option>
              <option value="price-desc">Price (High to Low)</option>
            </select>
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/50"
            >
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </label>
        </div>

        {error && cases.length === 0 ? (
          <div className="rounded-2xl border border-red-400/20 bg-red-400/5 px-6 py-12 text-center text-red-100/80">
            {error}
          </div>
        ) : loading && cases.length === 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
              <div key={item} className="h-80 animate-pulse rounded-xl border border-[#2A2D31]/40 bg-[#1A1D21]/40" />
            ))}
          </div>
        ) : cases.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {cases.map((item, index) => (
                <CaseDetails
                  key={item._id || `${item.Title}-${index}`}
                  image={item.Image}
                  title={item.Title}
                  website={item.Website}
                  price={item.Price}
                  rtp={item.RTP}
                  game={item.Game || item.game}
                  subpageLinks={item.subpageLinks}
                />
              ))}
            </div>

            {error && (
              <p className="mt-6 text-center text-sm text-red-200/80">{error}</p>
            )}

            <LoadMore
              currentItems={cases.length}
              totalItems={pagination.total}
              loading={loading}
              onLoadMore={handleLoadMore}
            />
          </>
        ) : (
          <div className="rounded-2xl border border-[#2A2D31]/50 bg-[#0F1215]/60 px-6 py-14 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF9A3D]">
              <Search className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-semibold text-white">No cases found</h3>
            <p className="mt-1 text-sm text-blue-100/60">Try a different search or sorting option.</p>
          </div>
        )}
      </section>
    </div>
  )
}

export default CasesPage
